import { type Chart, type ChartData, type ChartOptions } from 'chart.js';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import { BLUE_COLOR } from '../../../../components/reports/common/color.constants';
import {
  emphasizeColor,
  fadeColor,
  getAspectRatio,
  getLegend,
} from '../../../../components/reports/common/helpers/chart-options.helper';
import {
  DateZoomRange,
  useOnZoomComplete,
  useZoomSetupResetCallback,
} from '../../../../components/reports/common/hooks/use-zoom.hook';
import {
  DragBoxPlugin,
  DragBoxPluginChart,
} from '../../../../components/reports/common/plugins/draw-box.plugin';
import { LegendPaddingPlugin } from '../../../../components/reports/common/plugins/legend-padding.plugin';
import { ExportButtonDownloader } from '../../../../components/reports/components/export-button/export-button.component';
import { usePDFDownloader } from '../../../../components/reports/hooks/use-pdf-downloader.hook';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { useResize } from '../../../../hooks/use-window-resize.hook';
import { SortBy } from '../../../../interfaces/sort-by.interface';
import {
  formatBigNumber,
  formatNumber,
} from '../../../../utils/formatting/number-formatter.helper';
import 'chart.js/auto';
import './decomp-model-chart-component.scss';
import '../../../../components/reports/scss/chart-component.scss';

const VARIABLE_MEASURE_INDEX = 2;
interface DecompModelChartComponentProps extends ReportsComponentProps {
  defaultSortBy?: SortBy[];
  setPDFDownloader?(callback: ExportButtonDownloader): void;
  setZoom?(range: DateZoomRange | undefined): void;
  setResetZoomCallback?(resetZoom: DragBoxPluginChart): void;
}
const colorArray = [
  BLUE_COLOR, //Blue
  // 'rgba(0, 123, 0, 1)',       // Green
  '#000', // Black
  'rgba(255, 204, 0, 1)', // Yellow
  'rgba(75, 192, 192, 1)', // Teal
  'rgba(255, 165, 0, 1)', // Orange
];
const DecompModelChartComponent = ({
  defaultSortBy,
  reportName: reportId,
  filterSelection,
  enabled,
  className,
  setPDFDownloader,
  setZoom,
  setResetZoomCallback,
}: DecompModelChartComponentProps) => {
  const [chartData, setChartData] = useState<ChartData<'line'>>({
    labels: [],
    datasets: [],
  });
  const [xAxisLabel, setXAxisLabel] = useState<string>('Week'); // Set from columns[0]
  const [yAxisLabel, setYAxisLabel] = useState<string>(''); // Will set based on selected measure
  const [y2AxisLabel, setY2AxisLabel] = useState<string>(''); // Will set based on selected variable
  const [y2BeginAtZero, setY2BeginAtZero] = useState<boolean>(false); // State for y2 beginAtZero
  const [y2Min, setY2Min] = useState<number | undefined>(undefined); // State for y2 min value
  const [y2Max, setY2Max] = useState<number | undefined>(undefined); // State for y2 max value
  const chartRef = useRef<Chart<'line'> | null>(null); // Ref for chart instance
  // Track highlighted datasets - when clicked on legend
  const [highlightedDatasets, setHighlightedDatasets] = useState<
    Record<number, boolean>
  >({});
  // Track if any dataset is currently highlighted
  const [hasHighlights, setHasHighlights] = useState<boolean>(false);

  // Use the useDataProvider hook to fetch the data, but only if enabled
  const { data, isFetching } = useDataProvider(
    reportId,
    filterSelection,
    defaultSortBy,
    enabled,
  );

  const pdfDownloader = usePDFDownloader(
    filterSelection,
    'IRIS-Decomp-model',
    'IRIS Decomp model',
    chartRef.current,
  );

  useEffect(() => {
    if (setPDFDownloader !== undefined) {
      setPDFDownloader(pdfDownloader);
    }
  }, [setPDFDownloader, pdfDownloader]);

  // Capture date range of zoom.
  const { onZoomComplete } = useOnZoomComplete(data, setZoom);

  // Setup callback to allow parent component to reset this chart.
  useZoomSetupResetCallback(
    chartRef.current as DragBoxPluginChart,
    setResetZoomCallback,
  );

  const transformData = useCallback(
    (data: any): ChartData<'line'> => {
      const xAxisColumn = data.columns?.[0]; // WEEK
      const xAxisId = xAxisColumn?.id || 'WEEK';
      const xAxisFormat = xAxisColumn?.format || 'YYYY-MM-DD';
      const xAxisDatatype = xAxisColumn?.datatype;

      const labels = Array.from(
        new Set(
          data.rows.map((row: any) => {
            const value = row[xAxisId];
            if (xAxisDatatype === 'DATETIME' && xAxisFormat) {
              return moment(value).format(xAxisFormat);
            }
            return value;
          }),
        ),
      );

      const datasets = data.measureColumns.map(
        (measure: any, index: number) => {
          const color = colorArray[index % colorArray.length]; // Use colors sequentially
          const isHighlighted = highlightedDatasets[index] || false;
          const shouldFade = hasHighlights && !isHighlighted;
          const yAxisID = index >= VARIABLE_MEASURE_INDEX ? 'y2' : 'y'; // Last element goes to y2

          // Base dataset
          const dataset: any = {
            label: measure.name, // Use name for the legend label
            data: data.rows.map((row: any) => row[measure.id]), // Use id to fetch the correct data
            borderColor: color,
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            pointBackgroundColor: color,
            pointBorderColor: color,
            fill: false,
            pointRadius: 3,
            pointHoverRadius: 6,
            yAxisID, // Assign the appropriate Y-axis (y or y2)
            borderWidth: 2,
            hidden: false, // Never hide the dataset
          };

          // Apply highlight styling if this dataset is highlighted
          if (isHighlighted) {
            dataset.borderWidth = 3; // Thicker line for highlighted datasets
            dataset.pointRadius = 4; // Larger points
            dataset.pointHoverRadius = 7; // Larger hover radius
            dataset.borderColor = emphasizeColor(color);
            dataset.pointBackgroundColor = emphasizeColor(color);
            dataset.pointBorderColor = emphasizeColor(color);
            dataset.zIndex = 10; // Bring to front
          }
          // Apply faded styling if other datasets are highlighted but not this one
          else if (shouldFade) {
            dataset.borderWidth = 1; // Thinner line
            dataset.pointRadius = 2; // Smaller points
            dataset.pointHoverRadius = 4; // Smaller hover radius
            dataset.borderColor = fadeColor(color);
            dataset.pointBackgroundColor = fadeColor(color);
            dataset.pointBorderColor = fadeColor(color);
            dataset.zIndex = 1; // Send to back
          }

          return dataset;
        },
      );

      return {
        labels: labels,
        datasets: datasets as ChartData<'line'>['datasets'],
      };
    },
    [highlightedDatasets, hasHighlights],
  );

  useEffect(() => {
    if (!enabled || !data) return; // Early return if data is undefined or the component is not enabled

    const transformedData = transformData(data);
    setChartData(transformedData);

    if (data.columns && data.columns.length > 0) {
      setXAxisLabel(data.columns[0].name); // Use the name from data.columns[0] for the X-axis label
    }
    if (data.measureColumns && data.measureColumns.length > 0) {
      setYAxisLabel(data.measureColumns[0].name); // Set the Y-axis label based on measureColumns[0] initially
    }
    if (
      data.measureColumns &&
      data.measureColumns.length - 1 >= VARIABLE_MEASURE_INDEX
    ) {
      const exactOneVariable =
        data.measureColumns.length - 1 === VARIABLE_MEASURE_INDEX;
      setY2AxisLabel(
        exactOneVariable
          ? data.measureColumns[VARIABLE_MEASURE_INDEX].name
          : 'Selected Variable',
      ); // Set the Y2-axis label based on measureColumns

      // Extract beginAtZero, min, and max settings from the measure at VARIABLE_MEASURE_INDEX
      const measure = data.measureColumns[VARIABLE_MEASURE_INDEX];
      const beginAtZero = measure?.format?.beginAtZero === true;
      const min =
        measure?.format?.min !== undefined ? measure.format.min : undefined;
      const max =
        measure?.format?.max !== undefined ? measure.format.max : undefined;

      setY2BeginAtZero(beginAtZero);
      setY2Min(min);
      setY2Max(max);
    }
  }, [enabled, data, transformData, highlightedDatasets, hasHighlights]);

  // Check if any dataset is highlighted whenever highlightedDatasets changes
  useEffect(() => {
    const anyHighlighted = Object.values(highlightedDatasets).some(
      (value) => value,
    );
    setHasHighlights(anyHighlighted);
  }, [highlightedDatasets]);

  // Handle legend click event
  const handleLegendClick = useCallback((e: MouseEvent, legendItem: any) => {
    if (legendItem && typeof legendItem.datasetIndex === 'number') {
      const index = legendItem.datasetIndex;

      setHighlightedDatasets((prev) => {
        const newState = { ...prev };

        // If clicking an already highlighted item, unhighlight it
        if (newState[index]) {
          delete newState[index];
        } else {
          // Otherwise highlight it
          newState[index] = true;
        }

        return newState;
      });
    }

    return false; // Prevent default behavior
  }, []);

  const options: ChartOptions<'line'> = useMemo(() => {
    // Get base legend options
    const legendOptions = getLegend(30, handleLegendClick);

    // Enhanced legend labels with custom styling for highlighted items
    const enhancedLegendOptions = {
      ...legendOptions,
      labels: {
        ...legendOptions.labels,
        filter: (legendItem: any) => {
          const index = legendItem.datasetIndex;
          const isHighlighted =
            index !== undefined && highlightedDatasets[index];

          if (isHighlighted) {
            // Make highlighted legend items bold or otherwise distinguished
            legendItem.fontColor = '#000'; // Darker text
            legendItem.fontStyle = 'bold';
          } else if (hasHighlights) {
            // Dim the non-highlighted legend items
            legendItem.fontColor = 'rgba(0, 0, 0, 0.5)';
          }

          return true; // Keep all legend items
        },
      },
    };

    return {
      responsive: true,
      aspectRatio: getAspectRatio(),
      scales: {
        x: {
          title: {
            display: true,
            text: xAxisLabel,
            font: {
              size: 10,
            },
          },
          ticks: {
            font: {
              size: 9,
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          beginAtZero: true, // Always show zero
          title: {
            display: true,
            text: yAxisLabel,
            font: {
              size: 10,
            },
          },
          ticks: {
            font: {
              size: 9, // Reduced font size for Y-axis labels
            },
            callback: function (tickValue: any) {
              return formatBigNumber(tickValue); // Format Y-axis values
            },
          },
          grid: {
            display: false, // Remove the grid lines on the Y-axis
          },
        },
        y2: {
          beginAtZero: y2BeginAtZero, // Using the value from API response
          suggestedMin: y2Min, // Using min value from API response if provided
          suggestedMax: y2Max, // Using max value from API response if provided
          title: {
            display: true,
            text: y2AxisLabel,
            font: {
              size: 10,
            },
          },
          position: 'right',
          ticks: {
            font: {
              size: 9,
            },
          },
          grid: {
            display: false, // Remove the grid lines on the y2-axis
          },
        },
      },
      elements: {
        line: {
          tension: 0.4, // Smoother lines
          borderColor: 'rgba(0, 123, 255, 0.8)', // Set line border color to visible
          backgroundColor: 'rgba(0, 123, 255, 0.1)', // Set line background color to visible
        },
        point: {
          borderWidth: 1, // Set point border width
          radius: 2, // Set point radius
          hoverRadius: 3, // Increase point radius on hover
        },
      },
      plugins: {
        drawBox: {
          onComplete: onZoomComplete,
          borderWidth: 1,
        },
        legend: enhancedLegendOptions,
        tooltip: {
          enabled: true, // Enable tooltips on hover
          mode: 'nearest',
          intersect: false, // Tooltip appears for nearest point, even if not intersecting
          callbacks: {
            label: function (context: any) {
              const label = context.dataset.label || '';
              const value = context.raw;

              // Check if the tooltip is for the `y2` axis
              if (context.dataset.yAxisID === 'y2') {
                return `${label}: ${formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
              } else {
                return `${label}: ${formatBigNumber(value)}`;
              }
            },
          },
        },
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
        },
      },
    };
  }, [
    onZoomComplete,
    xAxisLabel,
    y2AxisLabel,
    yAxisLabel,
    handleLegendClick,
    highlightedDatasets,
    hasHighlights,
    y2BeginAtZero,
    y2Min,
    y2Max,
  ]);

  useResize(chartRef.current);

  if (!enabled) {
    return <div className="chart-component__disabled">Chart is disabled</div>;
  }

  return (
    <div
      className={`chart-component ${className ?? ''}`}
      style={{ backgroundColor: 'transparent', border: 'none' }}
    >
      <LoadingComponent isLoading={isFetching}>
        <Line
          ref={chartRef}
          data={chartData}
          options={options}
          plugins={[LegendPaddingPlugin, DragBoxPlugin]}
        />
      </LoadingComponent>
    </div>
  );
};

export default DecompModelChartComponent;
